import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  LogOut, RefreshCw, Users, Mail, MessageSquare, LayoutDashboard, FolderOpen,
  School as SchoolIcon, Wallet, UserCog, Megaphone, Upload, Download, Trash2,
  Filter, Search, PlusCircle, HeartHandshake,
} from "lucide-react";

// ---------- Login ----------
function Login({ onLoggedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      toast.success(`Welcome, ${r.data?.user?.name || "friend"}!`);
      onLoggedIn();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-cream-soft flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-5xl bg-white rounded-md shadow-2xl overflow-hidden grid lg:grid-cols-[1.05fr_1fr]">
        {/* Left brand panel */}
        <div className="relative bg-navy text-white p-8 md:p-12 flex flex-col overflow-hidden">
          <div className="absolute inset-0 opacity-15 pointer-events-none" aria-hidden style={{
            background: "radial-gradient(circle at 20% 20%, #2FBFB1 0%, transparent 55%), radial-gradient(circle at 80% 80%, #FF5757 0%, transparent 50%)",
          }} />
          <Link to="/" data-testid="cms-back-home" className="relative flex items-center gap-3">
            <img src="/images/akshara-logo.png" alt="Akshara Daana" className="h-16 md:h-20 w-auto brightness-0 invert" />
          </Link>
          <div className="relative mt-8">
            <div className="text-teal font-display font-bold text-xs uppercase tracking-[0.28em]">
              Akshara Daana &middot; CMS
            </div>
            <h1 className="mt-3 font-display font-extrabold text-3xl md:text-4xl leading-tight">
              Every akshara is a seed we plant in a child&rsquo;s future.
            </h1>
            <p className="mt-4 font-display text-white/75 leading-relaxed max-w-md">
              Sign in to manage schools, documents, finances and the team behind the movement.
            </p>
          </div>

          <div className="relative mt-8 flex items-end gap-5 flex-1">
            <div className="w-32 h-40 md:w-40 md:h-52 overflow-hidden rounded-sm border-2 border-teal shadow-2xl shrink-0">
              <img src="/images/revathi-kamath.jpg" alt="Smt. Revathi Kamath" className="w-full h-full object-cover object-center" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-teal font-bold">Founder</div>
              <div className="font-display font-extrabold text-xl md:text-2xl leading-tight">Smt. Revathi Kamath</div>
              <div className="text-sm text-white/70 mt-1">Karnataka Kalashree Awardee</div>
              <div className="mt-3 quote text-sunflower text-sm md:text-base italic">
                &ldquo;Vidyādhanam Sarva Dhanam Pradhānam.&rdquo;
              </div>
            </div>
          </div>
        </div>

        {/* Right form panel */}
        <div className="p-8 md:p-12 flex flex-col justify-center">
          <h2 className="font-display font-extrabold text-navy text-2xl md:text-3xl">Sign in</h2>
          <p className="font-display text-navy/60 text-sm mt-1">to your Akshara Daana workspace</p>
          <form data-testid="admin-login-form" onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-widest font-bold text-navy/70">Email address</label>
              <input data-testid="admin-email" type="email" required placeholder="you@aksharadaana.org"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full bg-cream-soft border border-navy/15 focus:border-teal px-4 py-3 font-display text-sm text-navy focus:outline-none rounded-sm" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest font-bold text-navy/70">Password</label>
              <div className="mt-1 relative">
                <input data-testid="admin-password" type={showPw ? "text" : "password"} required placeholder="••••••••"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-cream-soft border border-navy/15 focus:border-teal px-4 py-3 pr-14 font-display text-sm text-navy focus:outline-none rounded-sm" />
                <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold uppercase tracking-widest text-teal">
                  {showPw ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            <button data-testid="admin-submit" type="submit" disabled={busy}
              className="btn-vermilion w-full justify-center disabled:opacity-60">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="mt-8 text-center text-xs font-display text-navy/50">
            Trouble signing in? Reach out to <a href="mailto:connect@aksharadaana.org" className="text-teal font-bold hover:underline">connect@aksharadaana.org</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Common helpers ----------
const NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["trustee", "admin", "department_user", "viewer"] },
  { key: "documents", label: "Documents", icon: FolderOpen, roles: ["trustee", "admin", "department_user", "viewer"] },
  { key: "schools", label: "Schools", icon: SchoolIcon, roles: ["trustee", "admin"] },
  { key: "finance", label: "Finance", icon: Wallet, roles: ["trustee"] },
  { key: "donations", label: "Donations", icon: HeartHandshake, roles: ["trustee", "admin"] },
  { key: "team", label: "Team", icon: Users, roles: ["trustee", "admin"] },
  { key: "announcements", label: "Announcements", icon: Megaphone, roles: ["trustee", "admin"] },
  { key: "enrolments", label: "Enrolments", icon: Mail, roles: ["trustee", "admin"] },
  { key: "users", label: "Users", icon: UserCog, roles: ["trustee", "admin"] },
];

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "—");
const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const bytes = (n) => { const u = ["B", "KB", "MB", "GB"]; let i = 0; while (n >= 1024 && i < 3) { n /= 1024; i++; } return `${n.toFixed(1)} ${u[i]}`; };

function SectionShell({ title, subtitle, actions, children, testId }) {
  return (
    <section data-testid={testId} className="p-6 md:p-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display font-extrabold text-navy text-2xl md:text-3xl">{title}</h1>
          {subtitle && <p className="mt-1 font-display text-navy/60 text-sm">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

// ---------- Dashboard ----------
function Dashboard({ user }) {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/admin/summary").then((r) => setS(r.data)).catch(() => {}); }, []);
  const cards = [
    { k: "Volunteers", v: s.volunteers, tone: "coral" },
    { k: "Newsletter", v: s.newsletter, tone: "teal" },
    { k: "Contact Messages", v: s.contacts, tone: "sunflower" },
    { k: "Schools", v: s.schools, tone: "teal" },
    { k: "Documents", v: s.documents, tone: "coral" },
    { k: "Users", v: s.users, tone: "sunflower" },
    { k: "Team", v: s.team, tone: "teal" },
    { k: "Announcements", v: s.announcements, tone: "coral" },
  ];
  return (
    <SectionShell testId="cms-dashboard" title={`Welcome, ${user.name || user.email}`} subtitle={`Signed in as ${user.role} · ${(user.departments || []).join(", ") || "no departments assigned"}`}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.k} className="manuscript-card">
            <div className={`text-xs uppercase tracking-widest font-display font-bold ${c.tone === "coral" ? "text-coral" : c.tone === "sunflower" ? "text-sunflower-dark" : "text-teal"}`}>{c.k}</div>
            <div className="stat-num text-4xl text-navy mt-2">{c.v ?? 0}</div>
          </div>
        ))}
        {user.role === "trustee" && (
          <>
            <div className="manuscript-card sm:col-span-2 bg-teal text-white border-teal">
              <div className="text-xs uppercase tracking-widest font-display font-bold text-white/85">Income (all time)</div>
              <div className="stat-num text-4xl mt-2">{fmtINR(s.finance_income)}</div>
            </div>
            <div className="manuscript-card sm:col-span-2 bg-coral text-white border-coral">
              <div className="text-xs uppercase tracking-widest font-display font-bold text-white/85">Expense (all time)</div>
              <div className="stat-num text-4xl mt-2">{fmtINR(s.finance_expense)}</div>
            </div>
          </>
        )}
      </div>
    </SectionShell>
  );
}

// ---------- Documents ----------
function Documents({ user, config }) {
  const [docs, setDocs] = useState([]);
  const [dept, setDept] = useState("");
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [showUp, setShowUp] = useState(false);
  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (dept) p.set("department", dept);
    if (cat) p.set("category", cat);
    if (q) p.set("q", q);
    const r = await api.get("/cms/documents?" + p.toString());
    setDocs(r.data);
  }, [dept, cat, q]);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => {
    if (!window.confirm("Delete this file? This cannot be undone.")) return;
    await api.delete(`/cms/documents/${id}`); toast.success("Deleted"); load();
  };
  const download = async (d) => {
    const res = await api.get(`/cms/documents/${d.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = d.filename; a.click();
    URL.revokeObjectURL(url);
  };
  const canWrite = user.role !== "viewer";
  return (
    <SectionShell testId="cms-documents" title="Documents Library" subtitle="Upload, organise and download anything the team needs — by department and category."
      actions={canWrite && <button data-testid="doc-upload-open" onClick={() => setShowUp(true)} className="btn-vermilion !py-2 !px-4 text-sm"><Upload className="w-4 h-4" /> Upload</button>}>
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-2 bg-white border border-navy/10 px-3 py-2 rounded-sm text-sm">
          <Filter className="w-4 h-4 text-teal" />
          <select data-testid="doc-filter-dept" value={dept} onChange={(e) => setDept(e.target.value)} className="bg-transparent font-display outline-none">
            <option value="">All departments</option>
            {config.departments.map((d) => <option key={d}>{d}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 bg-white border border-navy/10 px-3 py-2 rounded-sm text-sm">
          <Filter className="w-4 h-4 text-teal" />
          <select data-testid="doc-filter-cat" value={cat} onChange={(e) => setCat(e.target.value)} className="bg-transparent font-display outline-none">
            <option value="">All categories</option>
            {config.doc_categories.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 bg-white border border-navy/10 px-3 py-2 rounded-sm text-sm flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-teal" />
          <input data-testid="doc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search filename, description or tag…" className="bg-transparent font-display outline-none w-full" />
        </div>
      </div>
      <div className="bg-white border border-navy/10 rounded-sm overflow-hidden">
        <table className="w-full text-sm font-display">
          <thead className="bg-cream-soft text-navy/70">
            <tr>
              {["File", "Department", "Category", "Size", "Uploaded by", "When", ""].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-navy/50">No documents yet.</td></tr>
            )}
            {docs.map((d) => (
              <tr key={d.id} data-testid={`doc-row-${d.id}`} className="border-t border-navy/10 hover:bg-cream-soft">
                <td className="px-4 py-3">
                  <div className="font-bold text-navy">{d.filename}</div>
                  {d.description && <div className="text-xs text-navy/60">{d.description}</div>}
                  {d.tags?.length ? <div className="mt-1 flex flex-wrap gap-1">
                    {d.tags.map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 bg-teal/10 text-teal rounded-sm">#{t}</span>)}
                  </div> : null}
                </td>
                <td className="px-4 py-3 text-navy/80">{d.department}</td>
                <td className="px-4 py-3 text-navy/80">{d.category}</td>
                <td className="px-4 py-3 text-navy/70">{bytes(d.size)}</td>
                <td className="px-4 py-3 text-navy/70">{d.uploaded_by_email}</td>
                <td className="px-4 py-3 text-navy/60">{fmtDate(d.created_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button data-testid={`doc-download-${d.id}`} onClick={() => download(d)} className="text-teal hover:underline text-xs uppercase tracking-widest font-bold mr-3"><Download className="w-4 h-4 inline" /></button>
                  {["admin", "trustee"].includes(user.role) && (
                    <button data-testid={`doc-delete-${d.id}`} onClick={() => del(d.id)} className="text-coral hover:underline text-xs uppercase tracking-widest font-bold"><Trash2 className="w-4 h-4 inline" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showUp && <UploadDialog config={config} onDone={() => { setShowUp(false); load(); }} onCancel={() => setShowUp(false)} />}
    </SectionShell>
  );
}

function UploadDialog({ config, onDone, onCancel }) {
  const [file, setFile] = useState(null);
  const [dept, setDept] = useState(config.departments[0]);
  const [cat, setCat] = useState(config.doc_categories[0]);
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please choose a file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("department", dept); fd.append("category", cat);
      fd.append("description", description); fd.append("tags", tags);
      await api.post("/cms/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-navy/60 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} data-testid="doc-upload-form" className="bg-white w-full max-w-lg p-6 rounded-sm shadow-2xl">
        <h2 className="font-display font-extrabold text-navy text-xl">Upload Document</h2>
        <p className="text-navy/60 text-sm mt-1">Max 25 MB per file.</p>
        <div className="mt-4 space-y-3">
          <input data-testid="doc-file" type="file" required onChange={(e) => setFile(e.target.files?.[0])} className="w-full font-display text-sm border border-navy/15 px-3 py-2 rounded-sm" />
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="doc-dept" value={dept} onChange={(e) => setDept(e.target.value)} className="border border-navy/15 px-3 py-2 rounded-sm font-display text-sm">
              {config.departments.map((d) => <option key={d}>{d}</option>)}
            </select>
            <select data-testid="doc-cat" value={cat} onChange={(e) => setCat(e.target.value)} className="border border-navy/15 px-3 py-2 rounded-sm font-display text-sm">
              {config.doc_categories.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <input data-testid="doc-tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Tags (comma separated)" className="w-full border border-navy/15 px-3 py-2 rounded-sm font-display text-sm" />
          <textarea data-testid="doc-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description" rows={3} className="w-full border border-navy/15 px-3 py-2 rounded-sm font-display text-sm" />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="btn-outline-indigo !py-2 !px-4 text-sm">Cancel</button>
          <button type="submit" disabled={busy} className="btn-vermilion !py-2 !px-4 text-sm disabled:opacity-60">{busy ? "Uploading…" : "Upload"}</button>
        </div>
      </form>
    </div>
  );
}

// ---------- Schools ----------
function Schools() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", location: "", status: "in_progress", prime_donor: "", description: "" });
  const load = () => api.get("/cms/schools").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    await api.post("/cms/schools", form);
    toast.success("School added"); setForm({ name: "", location: "", status: "in_progress", prime_donor: "", description: "" }); load();
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/cms/schools/${id}`); load(); };
  return (
    <SectionShell testId="cms-schools" title="Schools" subtitle="Every school Akshara Daana is engaged with.">
      <form onSubmit={add} className="manuscript-card mb-6 grid md:grid-cols-2 gap-3">
        <input data-testid="school-name" required placeholder="School name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm" />
        <input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm" />
        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm">
          <option value="planned">Planned</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
        </select>
        <input placeholder="Prime donor" value={form.prime_donor} onChange={(e) => setForm({ ...form, prime_donor: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm" />
        <textarea placeholder="Short description / works being done" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm md:col-span-2" />
        <button className="btn-vermilion md:col-span-2 justify-center"><PlusCircle className="w-4 h-4" /> Add school</button>
      </form>
      <div className="grid md:grid-cols-2 gap-4">
        {rows.length === 0 && <div className="text-navy/50 col-span-2 text-center py-6">No schools yet.</div>}
        {rows.map((s) => (
          <div key={s.id} data-testid={`school-${s.id}`} className="manuscript-card">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display font-extrabold text-navy text-lg">{s.name}</div>
                <div className="text-xs uppercase tracking-widest text-teal font-bold">{s.location}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm ${s.status === "completed" ? "bg-teal text-white" : s.status === "in_progress" ? "bg-sunflower text-navy" : "bg-navy/10 text-navy"}`}>{s.status.replace("_", " ")}</span>
            </div>
            {s.description && <p className="mt-2 text-navy/75 text-sm">{s.description}</p>}
            {s.prime_donor && <div className="mt-3 text-xs text-coral font-bold uppercase tracking-widest">Prime donor: {s.prime_donor}</div>}
            <button onClick={() => del(s.id)} className="mt-3 text-coral text-xs uppercase tracking-widest font-bold hover:underline"><Trash2 className="w-4 h-4 inline" /> Delete</button>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

// ---------- Finance ----------
function Finance() {
  const [data, setData] = useState({ rows: [], totals: { income: 0, expense: 0, balance: 0 } });
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), type: "income", counterparty: "", amount: "", category: "", description: "" });
  const load = () => api.get("/cms/finance").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    await api.post("/cms/finance", { ...form, amount: Number(form.amount) });
    toast.success("Entry added"); setForm({ ...form, counterparty: "", amount: "", category: "", description: "" }); load();
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/cms/finance/${id}`); load(); };
  return (
    <SectionShell testId="cms-finance" title="Finance Ledger" subtitle="Trustee-only view. Log donations, grants and expenses.">
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="manuscript-card bg-teal text-white border-teal"><div className="text-xs uppercase font-bold text-white/80">Income</div><div className="stat-num text-3xl mt-1">{fmtINR(data.totals.income)}</div></div>
        <div className="manuscript-card bg-coral text-white border-coral"><div className="text-xs uppercase font-bold text-white/80">Expense</div><div className="stat-num text-3xl mt-1">{fmtINR(data.totals.expense)}</div></div>
        <div className="manuscript-card bg-navy text-white border-navy"><div className="text-xs uppercase font-bold text-white/80">Balance</div><div className="stat-num text-3xl mt-1">{fmtINR(data.totals.balance)}</div></div>
      </div>
      <form onSubmit={add} className="manuscript-card mb-6 grid md:grid-cols-6 gap-3">
        <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm" />
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm">
          <option value="income">Income</option><option value="expense">Expense</option>
        </select>
        <input required placeholder="Donor / vendor" value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm md:col-span-2" />
        <input required type="number" step="0.01" placeholder="Amount ₹" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm" />
        <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm" />
        <input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm md:col-span-5" />
        <button className="btn-vermilion md:col-span-1 justify-center">Add</button>
      </form>
      <div className="bg-white border border-navy/10 rounded-sm overflow-hidden">
        <table className="w-full text-sm font-display">
          <thead className="bg-cream-soft text-navy/70">
            <tr>{["Date", "Type", "Counterparty", "Category", "Description", "Amount", ""].map((h) => <th key={h} className="text-left px-3 py-3 text-xs uppercase tracking-widest font-bold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-navy/50">No entries yet.</td></tr>}
            {data.rows.map((r) => (
              <tr key={r.id} className="border-t border-navy/10">
                <td className="px-3 py-2">{r.date}</td>
                <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm ${r.type === "income" ? "bg-teal/10 text-teal" : "bg-coral/10 text-coral"}`}>{r.type}</span></td>
                <td className="px-3 py-2 font-bold text-navy">{r.counterparty}</td>
                <td className="px-3 py-2 text-navy/80">{r.category}</td>
                <td className="px-3 py-2 text-navy/70">{r.description}</td>
                <td className="px-3 py-2 stat-num text-navy">{fmtINR(r.amount)}</td>
                <td className="px-3 py-2 text-right"><button onClick={() => del(r.id)} className="text-coral text-xs"><Trash2 className="w-4 h-4 inline" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionShell>
  );
}

// ---------- Users ----------
function UsersMgmt({ user, config }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "department_user", departments: [], password: "" });
  const load = () => api.get("/cms/users").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const toggleDept = (d) => {
    setForm((f) => ({ ...f, departments: f.departments.includes(d) ? f.departments.filter((x) => x !== d) : [...f.departments, d] }));
  };
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cms/users", form);
      toast.success("User created"); setForm({ email: "", name: "", role: "department_user", departments: [], password: "" }); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/cms/users/${id}`); load(); };
  const toggleActive = async (u) => { await api.patch(`/cms/users/${u.id}`, { active: !u.active }); load(); };
  const canTrustee = user.role === "trustee";
  return (
    <SectionShell testId="cms-users" title="Team Access" subtitle="Add teammates. Assign role and departments.">
      <form onSubmit={add} className="manuscript-card mb-6 grid md:grid-cols-2 gap-3">
        <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm" />
        <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm" />
        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm">
          <option value="viewer">Viewer</option>
          <option value="department_user">Department user</option>
          <option value="admin">Admin</option>
          {canTrustee && <option value="trustee">Trustee</option>}
        </select>
        <input required placeholder="Initial password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm" />
        <div className="md:col-span-2">
          <div className="text-xs uppercase font-bold text-navy/60 tracking-widest mb-2">Departments</div>
          <div className="flex flex-wrap gap-2">
            {config.departments.map((d) => (
              <button type="button" key={d} onClick={() => toggleDept(d)} className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest rounded-sm border ${form.departments.includes(d) ? "bg-teal text-white border-teal" : "bg-white text-navy border-navy/20"}`}>{d}</button>
            ))}
          </div>
        </div>
        <button className="btn-vermilion md:col-span-2 justify-center">Create user</button>
      </form>
      <div className="bg-white border border-navy/10 rounded-sm overflow-hidden">
        <table className="w-full text-sm font-display">
          <thead className="bg-cream-soft text-navy/70">
            <tr>{["Email", "Name", "Role", "Departments", "Active", "Created", ""].map((h) => <th key={h} className="text-left px-3 py-3 text-xs uppercase tracking-widest font-bold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-t border-navy/10">
                <td className="px-3 py-2 font-bold text-navy">{u.email}</td>
                <td className="px-3 py-2">{u.name}</td>
                <td className="px-3 py-2"><span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm bg-teal/10 text-teal">{u.role}</span></td>
                <td className="px-3 py-2 text-navy/70 text-xs">{(u.departments || []).join(", ")}</td>
                <td className="px-3 py-2"><button onClick={() => toggleActive(u)} className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm ${u.active ? "bg-teal text-white" : "bg-navy/10 text-navy"}`}>{u.active ? "Active" : "Disabled"}</button></td>
                <td className="px-3 py-2 text-navy/60">{fmtDate(u.created_at)}</td>
                <td className="px-3 py-2 text-right"><button onClick={() => del(u.id)} className="text-coral text-xs"><Trash2 className="w-4 h-4 inline" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionShell>
  );
}

// ---------- Team & Announcements & Enrolments (simple lists) ----------
function TeamMgmt() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", role: "", bio: "", phone: "", order: 0 });
  const load = () => api.get("/cms/team").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); await api.post("/cms/team", form); toast.success("Added"); setForm({ name: "", role: "", bio: "", phone: "", order: 0 }); load(); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/cms/team/${id}`); load(); };
  return (
    <SectionShell testId="cms-team" title="Team Members" subtitle="Manage the people shown in ‘The Hands Behind the Movement’.">
      <form onSubmit={add} className="manuscript-card mb-6 grid md:grid-cols-2 gap-3">
        <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm" />
        <input required placeholder="Role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm" />
        <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm" />
        <input type="number" placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm" />
        <textarea placeholder="Bio" rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="md:col-span-2 border border-navy/15 px-3 py-2 rounded-sm text-sm" />
        <button className="btn-vermilion md:col-span-2 justify-center">Add member</button>
      </form>
      <div className="grid md:grid-cols-2 gap-4">
        {rows.map((m) => (
          <div key={m.id} className="manuscript-card">
            <div className="font-display font-extrabold text-navy text-lg">{m.name}</div>
            <div className="text-xs uppercase tracking-widest text-teal font-bold">{m.role}</div>
            {m.bio && <p className="mt-2 text-sm text-navy/70">{m.bio}</p>}
            {m.phone && <div className="mt-2 text-xs text-navy/60">{m.phone}</div>}
            <button onClick={() => del(m.id)} className="mt-3 text-coral text-xs uppercase tracking-widest font-bold"><Trash2 className="w-4 h-4 inline" /> Delete</button>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

function Announcements() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ text: "", link: "#", active: true });
  const load = () => api.get("/cms/announcements").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); await api.post("/cms/announcements", form); toast.success("Added"); setForm({ text: "", link: "#", active: true }); load(); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/cms/announcements/${id}`); load(); };
  return (
    <SectionShell testId="cms-announcements" title="Announcements" subtitle="Text lines you can broadcast on the site announcement bar.">
      <form onSubmit={add} className="manuscript-card mb-6 grid gap-3">
        <input required placeholder="Announcement text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm" />
        <input placeholder="Link (anchor or URL)" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className="border border-navy/15 px-3 py-2 rounded-sm text-sm" />
        <button className="btn-vermilion justify-center">Add announcement</button>
      </form>
      <div className="space-y-2">
        {rows.map((a) => (
          <div key={a.id} className="manuscript-card flex items-center justify-between">
            <div><div className="font-bold text-navy">{a.text}</div><div className="text-xs text-navy/50">{a.link} · {fmtDate(a.created_at)}</div></div>
            <button onClick={() => del(a.id)} className="text-coral text-xs uppercase tracking-widest font-bold"><Trash2 className="w-4 h-4 inline" /></button>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

function Donations() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/donations").then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => {
    if (status !== "all" && r.status !== status) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (r.name || "").toLowerCase().includes(s) ||
      (r.email || "").toLowerCase().includes(s) ||
      (r.phone || "").toLowerCase().includes(s) ||
      (r.pan || "").toLowerCase().includes(s) ||
      (r.order_id || "").toLowerCase().includes(s) ||
      (r.payment_id || "").toLowerCase().includes(s)
    );
  });

  const totals = {
    count: filtered.length,
    paidCount: filtered.filter((r) => r.status === "paid").length,
    paidAmount: filtered.filter((r) => r.status === "paid").reduce((a, r) => a + (r.amount || 0), 0),
  };

  const exportCSV = () => {
    const cols = ["created_at", "status", "name", "email", "phone", "amount", "currency", "pan", "order_id", "payment_id", "message"];
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [cols.join(",")];
    filtered.forEach((r) => lines.push(cols.map((c) => esc(r[c])).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `donations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} donations`);
  };

  const statusPill = (s) => {
    const map = {
      paid: "bg-teal/15 text-teal border-teal/30",
      created: "bg-sunflower/20 text-navy border-sunflower/40",
      signature_failed: "bg-coral/15 text-coral border-coral/30",
    };
    return `inline-block px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest font-bold border ${map[s] || "bg-navy/5 text-navy/70 border-navy/15"}`;
  };

  return (
    <SectionShell
      testId="cms-donations"
      title="Donations"
      subtitle="Every gift routed through Razorpay lands here. Filter, search and export as CSV."
      actions={
        <>
          <button data-testid="donations-refresh" onClick={load} className="btn-outline-indigo !py-2 !px-3 text-xs"><RefreshCw className="w-3 h-3" /> Refresh</button>
          <button data-testid="donations-export" onClick={exportCSV} className="btn-vermilion !py-2 !px-3 text-xs"><Download className="w-3 h-3" /> Export CSV</button>
        </>
      }
    >
      {/* Totals */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="manuscript-card !p-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-navy/50">Filtered · total</div>
          <div className="stat-num text-2xl text-navy mt-1">{totals.count}</div>
        </div>
        <div className="manuscript-card !p-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-teal">Paid · count</div>
          <div className="stat-num text-2xl text-navy mt-1">{totals.paidCount}</div>
        </div>
        <div className="manuscript-card !p-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-coral">Paid · amount</div>
          <div className="stat-num text-2xl text-navy mt-1">{fmtINR(totals.paidAmount)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
          <input
            data-testid="donations-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone, PAN, order/payment id…"
            className="w-full pl-9 pr-3 py-2 border border-navy/15 rounded-sm font-display text-sm focus:outline-none focus:border-teal"
          />
        </div>
        <select
          data-testid="donations-status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-2 border border-navy/15 rounded-sm font-display text-sm bg-white text-navy focus:outline-none focus:border-teal"
        >
          <option value="all">All statuses</option>
          <option value="paid">Paid</option>
          <option value="created">Pending</option>
          <option value="signature_failed">Signature failed</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-navy/10 rounded-sm overflow-x-auto">
        <table className="w-full text-sm font-display">
          <thead className="bg-cream-soft text-navy/70">
            <tr>
              {["Date", "Status", "Name", "Email", "Phone", "Amount", "PAN", "Order / Payment"].map((c) => (
                <th key={c} className="text-left px-3 py-3 text-xs uppercase tracking-widest font-bold whitespace-nowrap">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="text-center py-10 text-navy/50">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-navy/50">No donations match.</td></tr>}
            {filtered.map((r) => (
              <tr key={r.id || r.order_id} data-testid={`donation-row-${r.order_id}`} className="border-t border-navy/10 hover:bg-cream-soft/60">
                <td className="px-3 py-2.5 text-navy/85 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                <td className="px-3 py-2.5"><span className={statusPill(r.status)}>{r.status || "—"}</span></td>
                <td className="px-3 py-2.5 text-navy font-bold">{r.name || "—"}</td>
                <td className="px-3 py-2.5 text-navy/80">{r.email || "—"}</td>
                <td className="px-3 py-2.5 text-navy/80 whitespace-nowrap">{r.phone || "—"}</td>
                <td className="px-3 py-2.5 stat-num text-navy">{fmtINR(r.amount)}</td>
                <td className="px-3 py-2.5 text-navy/70 uppercase text-xs">{r.pan || "—"}</td>
                <td className="px-3 py-2.5 text-navy/60 text-xs">
                  <div className="truncate max-w-[220px]" title={r.order_id}>{r.order_id || "—"}</div>
                  {r.payment_id && <div className="truncate max-w-[220px] text-teal" title={r.payment_id}>{r.payment_id}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionShell>
  );
}

function Enrolments() {
  const [tab, setTab] = useState("volunteers");
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/admin/${tab}`).then((r) => setRows(r.data)); }, [tab]);
  const cols = {
    volunteers: ["name", "email", "phone", "city", "message", "created_at"],
    newsletter: ["email", "created_at"],
    contacts: ["name", "email", "subject", "message", "created_at"],
  }[tab];
  return (
    <SectionShell testId="cms-enrolments" title="Enrolments" subtitle="Public form submissions from the website.">
      <div className="flex gap-2 mb-5">
        {["volunteers", "newsletter", "contacts"].map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-xs uppercase tracking-widest font-bold rounded-sm ${tab === t ? "bg-navy text-white" : "bg-white text-navy border border-navy/15"}`}>{t}</button>
        ))}
      </div>
      <div className="bg-white border border-navy/10 rounded-sm overflow-hidden">
        <table className="w-full text-sm font-display">
          <thead className="bg-cream-soft text-navy/70">
            <tr>{cols.map((c) => <th key={c} className="text-left px-3 py-3 text-xs uppercase tracking-widest font-bold">{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || i} className="border-t border-navy/10">
                {cols.map((c) => <td key={c} className="px-3 py-2 text-navy/85 align-top">{c === "created_at" ? fmtDate(r[c]) : (r[c] || "—")}</td>)}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={cols.length} className="text-center py-8 text-navy/50">No entries yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </SectionShell>
  );
}

// ---------- Shell ----------
function CMSShell({ user, onLogout }) {
  const [tab, setTab] = useState("dashboard");
  const [config, setConfig] = useState({ departments: [], doc_categories: [] });
  useEffect(() => { api.get("/cms/config").then((r) => setConfig(r.data)); }, []);
  const items = NAV.filter((n) => n.roles.includes(user.role));

  return (
    <div className="min-h-screen bg-cream flex">
      <aside data-testid="cms-sidebar" className="w-64 bg-navy text-white flex-shrink-0 sticky top-0 h-screen overflow-y-auto">
        <Link to="/" className="flex items-center gap-3 px-6 py-6 border-b border-white/10">
          <img src="/images/akshara-logo.png" alt="Akshara Daana" className="h-14 w-auto brightness-0 invert" />
        </Link>
        <div className="p-3 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-teal font-bold">Signed in as</div>
          <div className="font-display font-bold text-sm mt-0.5 truncate">{user.name || user.email}</div>
          <div className="text-xs text-white/60 truncate">{user.email}</div>
          <div className="mt-1 inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 bg-teal text-white rounded-sm">{user.role}</div>
        </div>
        <nav className="py-2">
          {items.map((n) => {
            const Icon = n.icon;
            return (
              <button
                key={n.key}
                data-testid={`cms-nav-${n.key}`}
                onClick={() => setTab(n.key)}
                className={`w-full text-left px-6 py-2.5 flex items-center gap-3 text-sm font-display transition-colors ${tab === n.key ? "bg-teal text-white" : "hover:bg-white/5 text-white/85"}`}
              >
                <Icon className="w-4 h-4" />
                {n.label}
              </button>
            );
          })}
        </nav>
        <div className="p-4 mt-auto border-t border-white/10">
          <button data-testid="admin-logout" onClick={onLogout} className="w-full flex items-center gap-2 text-coral font-display font-bold text-sm">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        {tab === "dashboard" && <Dashboard user={user} />}
        {tab === "documents" && <Documents user={user} config={config} />}
        {tab === "schools" && <Schools />}
        {tab === "finance" && user.role === "trustee" && <Finance />}
        {tab === "donations" && <Donations />}
        {tab === "team" && <TeamMgmt />}
        {tab === "announcements" && <Announcements />}
        {tab === "enrolments" && <Enrolments />}
        {tab === "users" && <UsersMgmt user={user} config={config} />}
      </main>
    </div>
  );
}

// ---------- Entry ----------
export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const load = () => api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false)).finally(() => setChecking(false));
  useEffect(() => { load(); }, []);
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(false); toast.success("Signed out");
  };
  if (checking) return <div className="min-h-screen bg-cream flex items-center justify-center font-display text-navy/60">Checking session…</div>;
  if (!user) return <Login onLoggedIn={load} />;
  return <CMSShell user={user} onLogout={logout} />;
}
